import PropTypes from 'prop-types';
import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

const COPIED_RESET_MS = 2000;

const LABELS = {
    COPY: 'Copy',
    COPIED: 'Copied!',
    ERROR: 'Error',
};

const COPY_STATUS = {
    SUCCESS: 'success',
    ERROR: 'error',
};

const VARIANT = {
    BUTTON: 'button',
    ICON: 'icon',
};

/**
 * Copy-to-clipboard control with brief success/error feedback (resets after {@link COPIED_RESET_MS}ms).
 * Labels come from {@link LABELS}; feedback state uses {@link COPY_STATUS} (`null` when idle).
 * `variant="button"`: contained neutral gray MUI button with `startIcon` and visible label.
 * `variant="icon"`: MUI `IconButton` with `Tooltip`. On success, shows {@link LABELS.COPIED} and `CheckIcon`;
 * on clipboard failure, shows {@link LABELS.ERROR} and `ErrorOutlineIcon`.
 * Pass `textToCopy` for clipboard content and `textToDisplay` for the visible label or tooltip/aria.
 *
 * Timeout id and mount flag live on the instance (`_timeoutId`, `_mounted`) so unmount cannot race
 * a setState that stores the timeout, and clipboard promise handlers skip setState after unmount.
 */
class ClickToCopyButton extends React.Component {
    constructor(props) {
        super(props);

        this.state = this.getInitialState();
        this._timeoutId = null;
        this._mounted = false;
    }

    static propTypes = {
        variant: PropTypes.oneOf(Object.values(VARIANT)),
        /** Optional hook before clipboard write (e.g. `event.stopPropagation()` in table rows). */
        beforeCopy: PropTypes.func,
        textToDisplay: PropTypes.string,
        textToCopy: PropTypes.string,
    };

    static defaultProps = {
        variant: VARIANT.BUTTON,
    };

    /**
     * Copies text to the clipboard; sets {@link COPY_STATUS} from the write promise (not optimistically).
     * Schedules the feedback reset timeout on the instance before the async write so unmount can always clear it.
     * @param {React.MouseEvent} event
     */
    onCopy = event => {
        if (this.props.beforeCopy) {
            this.props.beforeCopy(event);
        }

        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
        }

        this._timeoutId = setTimeout(() => {
            this._timeoutId = null;
            if (this._mounted) {
                this.setState({ copyStatus: null });
            }
        }, COPIED_RESET_MS);

        // Promise outcome drives copyStatus so a rejected write never flashes success.
        navigator.clipboard
            .writeText(this.props.textToCopy || '')
            .then(() => {
                if (this._mounted) {
                    this.setState({ copyStatus: COPY_STATUS.SUCCESS });
                }
            })
            .catch(() => {
                if (this._mounted) {
                    this.setState({ copyStatus: COPY_STATUS.ERROR });
                }
            });
    };

    /**
     * @returns {{ copyStatus: string|null }}
     */
    getInitialState() {
        return {
            copyStatus: null,
        };
    }

    /** Marks the instance mounted so clipboard/timeout callbacks may call setState. */
    componentDidMount = () => {
        this._mounted = true;
    };

    /**
     * Clears the pending feedback reset timeout and marks unmounted so late clipboard promises skip setState.
     */
    componentWillUnmount = () => {
        this._mounted = false;
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
        }
    };

    /**
     * Icon for the current {@link COPY_STATUS}: check on success, error outline on failure, copy otherwise.
     * @returns {React.ReactElement}
     */
    getStatusIcon() {
        const { copyStatus } = this.state;

        if (copyStatus === COPY_STATUS.SUCCESS) {
            return <CheckIcon fontSize="small" />;
        }

        if (copyStatus === COPY_STATUS.ERROR) {
            return <ErrorOutlineIcon fontSize="small" />;
        }

        return <FileCopyOutlinedIcon fontSize="small" />;
    }

    /**
     * Label or tooltip text for the current {@link COPY_STATUS}.
     * @param {string} displayLabel
     * @returns {string}
     */
    getStatusLabel(displayLabel) {
        const { copyStatus } = this.state;

        if (copyStatus === COPY_STATUS.SUCCESS) {
            return LABELS.COPIED;
        }

        if (copyStatus === COPY_STATUS.ERROR) {
            return LABELS.ERROR;
        }

        return displayLabel;
    }

    /** Renders MUI icon button; tooltip and icon reflect {@link COPY_STATUS}. */
    renderIconVariant() {
        const { textToDisplay } = this.props;
        const displayLabel = textToDisplay || LABELS.COPY;
        const tooltipTitle = this.getStatusLabel(displayLabel);

        return (
            <Tooltip title={<Typography variant="body1">{tooltipTitle}</Typography>}>
                <IconButton
                    size="small"
                    aria-label={displayLabel}
                    sx={{ p: 0.5, flexShrink: 0 }}
                    onClick={this.onCopy}
                >
                    {this.getStatusIcon()}
                </IconButton>
            </Tooltip>
        );
    }

    /**
     * Renders contained neutral gray MUI button. Hidden label grid reserves max width across
     * `textToDisplay`, {@link LABELS.COPIED}, and {@link LABELS.ERROR} so flex rows do not shift.
     */
    renderButtonVariant() {
        const { textToDisplay } = this.props;
        const displayLabel = textToDisplay || LABELS.COPY;
        const label = this.getStatusLabel(displayLabel);

        return (
            <Button
                size="small"
                variant="contained"
                color="inherit"
                onClick={this.onCopy}
                aria-label={displayLabel}
                startIcon={this.getStatusIcon()}
                sx={{ textTransform: 'none', flexShrink: 0 }}
            >
                <span style={{ display: 'inline-grid' }}>
                    <span style={{ gridArea: '1/1', visibility: 'hidden' }} aria-hidden="true">
                        {displayLabel}
                    </span>
                    <span style={{ gridArea: '1/1', visibility: 'hidden' }} aria-hidden="true">
                        {LABELS.COPIED}
                    </span>
                    <span style={{ gridArea: '1/1', visibility: 'hidden' }} aria-hidden="true">
                        {LABELS.ERROR}
                    </span>
                    <span style={{ gridArea: '1/1' }}>{label}</span>
                </span>
            </Button>
        );
    }

    /** Renders the configured button or icon copy control. */
    render() {
        if (this.props.variant === VARIANT.ICON) {
            return this.renderIconVariant();
        }

        return this.renderButtonVariant();
    }
}

export default ClickToCopyButton;
